import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    Music,
    MoreVertical,
    Pause,
    Play,
    Repeat,
    Shuffle,
    ListPlus,
    Heart,
    Pencil,
    Download,
    Ellipsis,
    CornerDownRight,
    User,
    Info,
    Trash2,
} from 'lucide-react';
import { AlbumCard } from '../components/PageComponents/cards/AlbumCard';
import { TrackMenu } from '../components/PageComponents/trackMenu/TrackMenu';
import { TrackInfoModal } from '../components/PageComponents/trackMenu/TrackInfoModal';
import { useTrackMenu } from '../hooks/useTrackMenu';
import { useDetailMenu } from '../hooks/useDetailMenu';
import { useFavourite } from '../hooks/useFavourite';
import { DetailMoreMenu, DetailMenuItem } from '../components/PageComponents/detailMenu/DetailMoreMenu';
import { AlbumEditModal } from '../components/PageComponents/editModal/AlbumEditModal';
import { EntityInfoModal } from '../components/PageComponents/editModal/EntityInfoModal';
import { Album } from '../components/PageComponents/albumList/album';
import {
    getAlbumByID,
    getAlbumList,
    getAlbumsByArtistID,
    getTracksForAlbum,
    isTrackInAlbum,
} from '../components/PageComponents/albumList/getAllAlbums';
import { Track } from '../components/PageComponents/trackList/track';
import { PlayerContext } from '../context/PlayerContext';
import { coverArtUrl } from '../config/images';
import { AlbumArt } from '../components/PageComponents/cards/AlbumArt';
import { API_BASE } from '../config/api';
import './Detail.css';

function formatDuration(ms: number): string {
    const totalSeconds = Math.floor(ms / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = (totalSeconds % 60).toString().padStart(2, '0');
    return `${minutes}:${seconds}`;
}


export default function AlbumDetail(): React.JSX.Element {
    const { id } = useParams();
    const navigate = useNavigate();
    const {
        currentSong,
        isPlaying,
        tracks,
        refreshTracks,
        setQueue,
        playTrackNow,
        togglePlay,
        toggleShuffle,
        repeatMode,
        setRepeatMode,
        addTracksToQueue,
        playTracksNext,
    } = useContext(PlayerContext);
    const { menu, openAtCursor, openAtElement, closeMenu, infoTrack, openTrackInfo, closeTrackInfo } = useTrackMenu();
    const { position: moreMenuPosition, openAtElement: openMoreMenu, close: closeMoreMenu } = useDetailMenu();
    const [album, setAlbum] = useState<Album | null>(null);
    const [allAlbums, setAllAlbums] = useState<Album[]>([]);
    const [relatedAlbums, setRelatedAlbums] = useState<Album[]>([]);
    const [loading, setLoading] = useState(true);
    // Cache-buster for the cover art: the URL is otherwise identical before
    // and after the edit modal changes the underlying file, so the browser
    // would keep showing its cached copy without this - see the identical
    // artVersion in artistDetail.tsx.
    const [artVersion, setArtVersion] = useState(0);
    const [editing, setEditing] = useState(false);
    const [showInfo, setShowInfo] = useState(false);
    const { favourited, toggle: toggleFavourite } = useFavourite('album', album?.getId());

    useEffect(() => {
        refreshTracks();
        (async () => {
            setLoading(true);
            const albumId = Number(id);
            const [a, list] = await Promise.all([getAlbumByID(albumId), getAlbumList()]);
            setAlbum(a);
            setAllAlbums(list);
            setArtVersion(0);

            const primaryArtistId = a?.getArtists()[0]?.id;
            setRelatedAlbums(
                primaryArtistId ? (await getAlbumsByArtistID(primaryArtistId)).filter((other) => other.getId() !== albumId) : []
            );
            setLoading(false);
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const artistNames = useMemo(() => (album ? album.getArtists().map((a) => a.title).join(', ') : ''), [album]);
    const albumArtistIds = useMemo(() => new Set(album?.getArtists().map((a) => a.id) ?? []), [album]);
    const artistName = useMemo(() => {
        if (!album) return '';
        const match = tracks.find((t) => t.getAlbum() === album.getTitle());
        return match?.getArtist() ?? '';
    }, [album, tracks]);

    const albumTracks = useMemo(() => {
        if (!album) return [];
        return tracks
            .filter((t) => t.getAlbum() === album.getTitle() && t.getArtist() === artistName)
            .sort((a, b) => (a.getTrackNumber() ?? 0) - (b.getTrackNumber() ?? 0));
    }, [album, artistName, tracks]);

    // Only grouped into per-disc sections once more than one disc is
    // actually present - a normal single-disc album (still the overwhelming
    // common case) renders its flat track list exactly as before.
    const discGroups = useMemo(() => {
        const discs = new Set(albumTracks.map((t) => t.getDiscNumber() ?? 1));
        if (discs.size <= 1) return null;
        const groups = new Map<number, Track[]>();
        for (const t of albumTracks) {
            const disc = t.getDiscNumber() ?? 1;
            if (!groups.has(disc)) groups.set(disc, []);
            groups.get(disc)!.push(t);
        }
        return Array.from(groups.entries()).sort(([a], [b]) => a - b);
    }, [albumTracks]);

    const nameRelatedAlbums = useMemo(() => {
        if (!artistName) return [];
        const otherAlbumTitles = new Set(
            tracks.filter((t) => t.getArtist() === artistName && t.getAlbum() !== album?.getTitle()).map((t) => t.getAlbum())
        );
        return allAlbums.filter((a) => otherAlbumTitles.has(a.getTitle()));
    }, [artistName, tracks, album, allAlbums]);

    const handlePlay = (track: Track) => {
        if (currentSong?.getId() === track.getId()) togglePlay();
        else playTrackNow(track);
    };

    // Discography sorting, shared with the artist page: newest first by
    // default, switchable; undated albums sink to the end either direction.
    const [relatedSort, setRelatedSort] = useState<'newest' | 'oldest' | 'title'>('newest');
    const relatedList = relatedAlbums.length > 0 ? relatedAlbums : nameRelatedAlbums;
    const sortedRelated = useMemo(() => {
        const copy = [...relatedList];
        if (relatedSort === 'title') {
            copy.sort((a, b) => a.getTitle().localeCompare(b.getTitle(), undefined, { sensitivity: 'base' }));
        } else {
            const dir = relatedSort === 'newest' ? -1 : 1;
            copy.sort((a, b) => {
                const da = a.getReleaseDate();
                const db = b.getReleaseDate();
                if (da === db) return 0;
                if (!da) return 1;
                if (!db) return -1;
                return da < db ? -dir : dir;
            });
        }
        return copy;
    }, [relatedList, relatedSort]);

    const handlePlayAlbum = () => {
        setQueue(albumTracks, 0);
    };

    // Shuffling "this album" always starts a fresh shuffled play of it,
    // rather than toggling shuffle on whatever queue happens to be current -
    // setQueue resets shuffle off, so immediately re-enabling it here shuffles
    // specifically these tracks.
    const handleShuffleTracks = (sectionTracks: Track[]) => {
        setQueue(sectionTracks, 0);
        toggleShuffle();
    };

    // "Loop" on an album/disc means the whole thing repeats when it ends,
    // not just the current track - starts playing it and sets the global
    // repeat mode to 'all' (repeatMode is player-wide state, the same as
    // shuffle; the next thing played elsewhere is unaffected until then).
    const handleLoopTracks = (sectionTracks: Track[]) => {
        setQueue(sectionTracks, 0);
        setRepeatMode('all');
    };

    const handleDelete = async () => {
        if (!album) return;
        if (!window.confirm(`Remove "${album.getTitle()}" from your library? Files on disk aren't touched, and you can restore it later from Settings → Library.`)) {
            return;
        }
        const response = await fetch(`${API_BASE}/api/album?id=${album.getId()}`, {
            method: 'DELETE',
            credentials: 'include',
        });
        if (response.ok) navigate('/albums');
    };

    const renderTrackRow = (track: Track) => {
        const isThisTrackPlaying = currentSong?.getId() === track.getId() && isPlaying;
        const trackArtists = track.getArtists();
        // Only shown when this track's own credits actually differ from the
        // album's - a compilation/collab track can feature different
        // artists than the album header, and that's exactly what this
        // surfaces; the overwhelming common case (every track credits the
        // same artist(s) as the album) stays uncluttered.
        const differsFromAlbum =
            trackArtists.length > 0 &&
            (trackArtists.length !== albumArtistIds.size || trackArtists.some((a) => !albumArtistIds.has(a.id)));
        return (
            <div
                key={track.getId()}
                className={`detailTrackRow ${currentSong?.getId() === track.getId() ? 'detailTrackRowActive' : ''}`}
                onClick={() => handlePlay(track)}
                onContextMenu={(e) => openAtCursor(e, track)}
            >
                <span className="detailTrackNumber">
                    <span className="detailTrackNumberDigit">{track.getTrackNumber() ?? ''}</span>
                    {isThisTrackPlaying ? (
                        <Pause className="detailTrackNumberPlayIcon" size={14} strokeWidth={2.75} fill="currentColor" />
                    ) : (
                        <Play className="detailTrackNumberPlayIcon" size={14} strokeWidth={2.75} fill="currentColor" />
                    )}
                </span>
                <span className="detailTrackTitle">
                    {track.getTitle()}
                    {differsFromAlbum && (
                        <span className="detailTrackArtists">
                            {trackArtists.map((a, i) => (
                                <span key={a.id}>
                                    {i > 0 && ', '}
                                    <span
                                        className="detailHeroSubLink"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            navigate(`/artists/${a.id}`);
                                        }}
                                    >
                                        {a.title}
                                    </span>
                                </span>
                            ))}
                        </span>
                    )}
                </span>
                <span className="detailTrackDuration">{formatDuration(track.getDuration())}</span>
                <button
                    className="detailTrackMenuButton"
                    onClick={(e) => openAtElement(e, track)}
                    aria-label={`More options for ${track.getTitle()}`}
                >
                    <MoreVertical size={16} strokeWidth={2.75} />
                </button>
            </div>
        );
    };

    if (loading) {
        return (
            <div className="pageContent">
                <div className="trackListEmpty">Loading album…</div>
            </div>
        );
    }

    if (!album) {
        return (
            <div className="pageContent">
                <div className="trackListEmpty">Album not found</div>
            </div>
        );
    }

    const art = coverArtUrl(album.getId(), artVersion);

    return (
        <div className="pageContent" >
            <div className="detailHero">
                <div className="detailHeroArt">
                    {art ? <AlbumArt img={art} title={album.getTitle()} iconSize={40} /> : <Music size={40} strokeWidth={2.75} />}
                </div>
                <div className="detailHeroMeta">
                    <span className="detailHeroKicker">Album</span>
                    <h1 className="detailHeroTitle">{album.getTitle()}</h1>
                    {albumTracks[0]?.getArtistId() ? (
                        <button
                            type="button"
                            className="detailHeroSubLink"
                            onClick={() => navigate(`/artists/${albumTracks[0].getArtistId()}`)}
                        >
                            {artistName}
                        </button>
                    ) : (
                        <span className="detailHeroSub">{artistName}</span>
                    )}
                    {album.getReleaseYear() && (
                        <span className="detailHeroMuted">
                            {[album.getReleaseYear(), album.getReleaseTypeLabel()].filter(Boolean).join(' · ')} ·{' '}
                            {albumTracks.length} track{albumTracks.length === 1 ? '' : 's'}
                        </span>
                    )}
                    {album.getGenres() && <span className="detailHeroMuted detailGenres">{album.getGenres()}</span>}
                    <div className="detailHeroActions">
                        {albumTracks.length > 0 && (
                            <>
                                <button className="detailPlayButton" onClick={handlePlayAlbum}>
                                    <Play size={16} strokeWidth={2.75} fill="currentColor" />
                                    Play Album
                                </button>
                                <button className="detailPillButton" onClick={() => handleShuffleTracks(albumTracks)}>
                                    <Shuffle size={15} strokeWidth={2.75} />
                                    Shuffle
                                </button>
                                <button
                                    className={`detailPillButton ${repeatMode !== 'off' ? 'detailPillButtonActive' : ''}`}
                                    onClick={() => handleLoopTracks(albumTracks)}
                                >
                                    <Repeat size={15} strokeWidth={2.75} />
                                    Repeat
                                </button>
                                <button className="detailPillButton" onClick={() => addTracksToQueue(albumTracks)}>
                                    <ListPlus size={15} strokeWidth={2.75} />
                                    Queue
                                </button>
                            </>
                        )}
                        <button
                            className={`detailIconButton ${favourited ? 'detailIconButtonActive' : ''}`}
                            onClick={toggleFavourite}
                            aria-label={favourited ? 'Remove from favourites' : 'Add to favourites'}
                        >
                            <Heart size={16} strokeWidth={2.75} fill={favourited ? 'currentColor' : 'none'} />
                        </button>
                        <button className="detailIconButton" onClick={() => setEditing(true)} aria-label="Edit album">
                            <Pencil size={16} strokeWidth={2.75} />
                        </button>
                        <a
                            className="detailIconButton"
                            href={`${API_BASE}/api/download?type=album&id=${album.getId()}`}
                            download
                            aria-label="Download album"
                        >
                            <Download size={16} strokeWidth={2.75} />
                        </a>
                        <button className="detailIconButton" onClick={openMoreMenu} aria-label="More options">
                            <Ellipsis size={16} strokeWidth={2.75} />
                        </button>
                    </div>
                </div>
            </div>

            <div className="detailTrackList">
                {discGroups
                    ? discGroups.map(([disc, discTracks]) => (
                          <div key={disc}>
                              <div className="detailDiscHeader">
                                  <span>Disc {disc}</span>
                                  <span className="detailDiscHeaderActions">
                                      <button
                                          className="detailDiscHeaderButton"
                                          onClick={() => setQueue(discTracks, 0)}
                                          aria-label={`Play disc ${disc}`}
                                      >
                                          <Play size={13} strokeWidth={2.75} fill="currentColor" />
                                      </button>
                                      <button
                                          className="detailDiscHeaderButton"
                                          onClick={() => handleShuffleTracks(discTracks)}
                                          aria-label={`Shuffle disc ${disc}`}
                                      >
                                          <Shuffle size={13} strokeWidth={2.75} />
                                      </button>
                                      <button
                                          className={`detailDiscHeaderButton ${
                                              repeatMode !== 'off' ? 'detailDiscHeaderButtonActive' : ''
                                          }`}
                                          onClick={() => handleLoopTracks(discTracks)}
                                          aria-label={`Repeat disc ${disc}`}
                                      >
                                          <Repeat size={13} strokeWidth={2.75} />
                                      </button>
                                  </span>
                              </div>
                              {discTracks.map(renderTrackRow)}
                          </div>
                      ))
                    : albumTracks.map(renderTrackRow)}
            </div>

            {menu && (
                <TrackMenu track={menu.track} x={menu.x} y={menu.y} onClose={closeMenu} onShowInfo={openTrackInfo} />
            )}
            {infoTrack && <TrackInfoModal track={infoTrack} onClose={closeTrackInfo} />}

            {editing && (
                <AlbumEditModal
                    album={album}
                    onClose={() => setEditing(false)}
                    onSaved={(updated) => {
                        setAlbum(updated);
                        setArtVersion((v) => v + 1);
                        refreshTracks();
                    }}
                />
            )}
            {showInfo && (
                <EntityInfoModal
                    title="Album info"
                    rows={[
                        ['Title', album.getTitle()],
                        ['Artist', artistName],
                        ['Release date', album.getReleaseDate()],
                        ['Tracks', String(albumTracks.length)],
                        ['MusicBrainz ID', album.getMbid()],
                    ]}
                    onClose={() => setShowInfo(false)}
                />
            )}
            {moreMenuPosition && (
                <DetailMoreMenu
                    x={moreMenuPosition.x}
                    y={moreMenuPosition.y}
                    onClose={closeMoreMenu}
                    tracks={albumTracks}
                    itemsBefore={[
                        { label: 'Play now', icon: Play, onClick: handlePlayAlbum },
                        { label: 'Play next', icon: CornerDownRight, onClick: () => playTracksNext(albumTracks) },
                        { label: 'Add to queue', icon: ListPlus, onClick: () => addTracksToQueue(albumTracks) },
                    ]}
                    itemsAfter={
                        [
                            albumTracks[0]?.getArtistId()
                                ? {
                                      label: 'Go to artist',
                                      icon: User,
                                      onClick: () => navigate(`/artists/${albumTracks[0].getArtistId()}`),
                                  }
                                : null,
                            {
                                label: 'Download',
                                icon: Download,
                                onClick: () => window.location.assign(`${API_BASE}/api/download?type=album&id=${album.getId()}`),
                            },
                            { label: 'Album info', icon: Info, onClick: () => setShowInfo(true) },
                            { label: 'Remove from library', icon: Trash2, onClick: handleDelete, danger: true },
                        ].filter(Boolean) as DetailMenuItem[]
                    }
                />
            )}

            {relatedList.length > 0 && (
                <div className="detailRelated">
                    <div className="detailRelatedHeader">
                        <h2 className="detailRelatedTitle">More from {artistNames}</h2>
                        <select
                            className="detailSortSelect"
                            value={relatedSort}
                            onChange={(e) => setRelatedSort(e.target.value as 'newest' | 'oldest' | 'title')}
                            aria-label="Sort albums"
                        >
                            <option value="newest">Newest first</option>
                            <option value="oldest">Oldest first</option>
                            <option value="title">Title A-Z</option>
                        </select>
                    </div>
                    <div className="cardGrid">
                        {sortedRelated.map((a) => {
                            const isThisAlbumCurrent = isTrackInAlbum(currentSong, a);
                            return (
                                <AlbumCard
                                    key={a.getId()}
                                    album={a}
                                    artistName={artistName}
                                    onClick={(clicked) => navigate(`/albums/${clicked.getId()}`)}
                                    isPlaying={isThisAlbumCurrent && isPlaying}
                                    onPlay={(clicked) => {
                                        if (isThisAlbumCurrent) togglePlay();
                                        else setQueue(getTracksForAlbum(tracks, clicked), 0);
                                    }}
                                />
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
}
